import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../components/ui"

const textPairings = [
  {
    id: "background-primary",
    label: "Primary text on background",
    sample: "The quick brown fox jumps over the lazy dog.",
    description: "Default body copy against the global background color.",
    backgroundVar: "--background-color",
    foregroundVar: "--text-color",
  },
  {
    id: "surface-primary",
    label: "Primary text on surface",
    sample: "Use for content areas and cards.",
    description: "Primary text on the mid-elevation surface color.",
    backgroundVar: "--surface-color",
    foregroundVar: "--text-color",
  },
  {
    id: "surface-muted",
    label: "Muted text on surface",
    sample: "Secondary copy, helper text, and labels.",
    description: "Muted text on the mid-elevation surface color.",
    backgroundVar: "--surface-color",
    foregroundVar: "--muted-text-color",
  },
  {
    id: "elevated-muted",
    label: "Muted text on elevated surface",
    sample: "Tertiary information, metadata, and table labels.",
    description: "Muted text on elevated surfaces, such as drawers or panels.",
    backgroundVar: "--surface-elevated-color",
    foregroundVar: "--muted-text-color",
  },
  {
    id: "accent-link",
    label: "Accent text on background",
    sample: "Interactive text and inline links use the accent color.",
    description: "Accent color against the global background.",
    backgroundVar: "--background-color",
    foregroundVar: "--primary-color",
  },
  {
    id: "cta",
    label: "CTA text on primary button",
    sample: "Primary actions rely on high contrast button text.",
    description: "Button text color layered on the primary gradient.",
    backgroundVar: "--primary-color",
    foregroundVar: "--button-text-color",
    border: "rgba(255, 255, 255, 0.25)",
  },
]

const fontStacks = [
  {
    id: "heading-font",
    label: "Heading font family",
    sample: "TuneUp Interface",
    description: "Applied to page titles, card headings, and navigation.",
    fontFamilyVar: "--heading-font-family",
  },
  {
    id: "body-font",
    label: "Body font family",
    sample: "The quick brown fox jumps over the lazy dog.",
    description: "Default family for paragraphs, tables, and controls.",
    fontFamilyVar: "--font-family",
  },
]

const fontWeights = [
  {
    id: "regular",
    label: "Regular 400",
    description: "Primary reading weight for paragraphs and long-form content.",
    weight: 400,
  },
  {
    id: "medium",
    label: "Medium 500",
    description: "Use for form labels, inline emphasis, and key-value pairs.",
    weight: 500,
  },
  {
    id: "semibold",
    label: "Semibold 600",
    description: "Default weight for card titles, headings, and navigation items.",
    weight: 600,
  },
  {
    id: "bold",
    label: "Bold 700",
    description: "Reserve for rare emphasis where additional contrast is needed.",
    weight: 700,
  },
]

const formatVar = (variable) => (variable ? `var(${variable})` : "")

export default function ThemeLibrary() {
  return (
    <div className="page-container page-container--wide ui-stack ui-stack--xl">
      <div className="ui-page-header">
        <div className="ui-stack ui-stack--xs">
          <h1 className="ui-page-title">Theme Library</h1>
          <p className="ui-subtitle">
            Review typography, text colors, and weights to maintain accessible
            contrast across the product.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Text color pairings</CardTitle>
          <CardDescription>
            Use these combinations to ensure text remains readable on the
            background or surface beneath it.
          </CardDescription>
        </CardHeader>
        <CardContent className="theme-token-grid">
          {textPairings.map((pair) => (
            <div
              key={pair.id}
              className="theme-token theme-token--color"
              style={{
                "--theme-token-bg": formatVar(pair.backgroundVar),
                "--theme-token-border": pair.border,
                color: formatVar(pair.foregroundVar),
              }}
            >
              <div className="theme-token__label">{pair.label}</div>
              <div className="theme-token__sample">{pair.sample}</div>
              <p className="theme-token__description">{pair.description}</p>
              <div className="theme-token__meta">
                <code>color: {formatVar(pair.foregroundVar)}</code>
                <code>background: {formatVar(pair.backgroundVar)}</code>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Font families</CardTitle>
          <CardDescription>
            Shared font stacks keep typography consistent across layouts and
            components.
          </CardDescription>
        </CardHeader>
        <CardContent className="theme-typography-grid">
          {fontStacks.map((stack) => (
            <div
              key={stack.id}
              className="theme-token theme-typography-sample"
              style={{
                fontFamily: formatVar(stack.fontFamilyVar),
              }}
            >
              <div className="theme-token__label">{stack.label}</div>
              <div className="theme-typography-sample__text">{stack.sample}</div>
              <p className="theme-token__description">{stack.description}</p>
              <div className="theme-token__meta">
                <code>font-family: {formatVar(stack.fontFamilyVar)}</code>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Font weights</CardTitle>
          <CardDescription>
            Pair these weights with the font stacks above to preserve hierarchy
            and accessibility.
          </CardDescription>
        </CardHeader>
        <CardContent className="theme-weights-grid">
          {fontWeights.map((weight) => (
            <div
              key={weight.id}
              className="theme-token theme-weight-sample"
              style={{
                fontFamily: formatVar("--font-family"),
                fontWeight: weight.weight,
              }}
            >
              <div className="theme-token__label">{weight.label}</div>
              <div className="theme-weight-sample__preview">
                The quick brown fox jumps over the lazy dog.
              </div>
              <p className="theme-token__description">{weight.description}</p>
              <div className="theme-token__meta">
                <code>font-weight: {weight.weight}</code>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}
